import { cn } from '@/utils/cn'
import {
  Pagination,
  PaginationButton,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from './pagination'

interface ListPaginationProps {
  currentPage: number
  itemsCount: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  className?: string
}

export const ListPagination = ({
  currentPage,
  itemsCount,
  itemsPerPage,
  onPageChange,
  className,
}: ListPaginationProps) => {
  const totalPages = Math.max(1, Math.ceil(itemsCount / itemsPerPage))

  return (
    <Pagination className={cn('flex-wrap justify-between gap-x-4 gap-y-2 px-4 py-3 md:px-5', className)}>
      <p className="text-xs">
        Page {currentPage} of {totalPages}
      </p>
      <PaginationContent>
        <PaginationItem>
          <PaginationPrevious onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} />
        </PaginationItem>
        <PaginationItems currentPage={currentPage} totalPages={totalPages} onPageChange={onPageChange} />
        <PaginationItem>
          <PaginationNext onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  )
}

interface PaginationItemsProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const PaginationItems = ({ currentPage, totalPages, onPageChange }: PaginationItemsProps) => {
  const pages = getPaginationPages(currentPage, totalPages)

  return (
    <>
      {pages.map((item, index) => {
        if (item === 'ellipsis') {
          return (
            <PaginationItem key={item + index}>
              <PaginationEllipsis key="ellipsis-start" />
            </PaginationItem>
          )
        }

        return (
          <PaginationItem key={item}>
            <PaginationButton onClick={() => onPageChange(item)} isActive={item === currentPage}>
              {item}
            </PaginationButton>
          </PaginationItem>
        )
      })}
    </>
  )
}

function getPaginationPages(currentPage: number, totalPages: number) {
  const paginationItems: (number | 'ellipsis')[] = []
  paginationItems.push(1)
  if (currentPage > 3) paginationItems.push('ellipsis')
  if (currentPage > 2) paginationItems.push(currentPage - 1)
  paginationItems.push(currentPage)
  if (currentPage < totalPages - 1) paginationItems.push(currentPage + 1)
  if (currentPage < totalPages - 2) paginationItems.push('ellipsis')
  if (totalPages > 1) paginationItems.push(totalPages)

  const seenPages = new Set<number>()
  const pages = paginationItems.filter((item) => {
    if (item === 'ellipsis' || !seenPages.has(item)) {
      if (typeof item === 'number') {
        seenPages.add(item)
      }
      return true
    }
    return false
  })

  return pages
}
